/**
 * @fileoverview UiShell component that provides the main layout structure for the application.
 * Handles responsive navigation, scroll-based animations, and layout management.
 */

import React, { useState } from "react";
import {
  AppBar,
  Drawer,
  Toolbar,
  IconButton,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { motion, useAnimation } from "framer-motion";
import { navItems } from "./UiShell.api";
import { LOGIN } from "../../constants/routes";
import { Footer } from "./Footer";
import { UiShellProps } from "./UiShell.types";
import { useScrollVisibility } from "./hooks/useScrollVisibility";
import { Logo } from "./components/Logo";
import { NavigationItems } from "./components/NavigationItems";
import { navigate } from "gatsby";

// Constants
const DRAWER_WIDTH = 240;
const SCROLL_THRESHOLD = 200;
const MOTION_INITIAL_Y = 0;
const MOTION_HIDDEN_Y = -100;

/**
 * UiShell Component
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render within the shell
 * @param {string} props.navItemColor - Color for navigation items
 */
export const UiShell: React.FC<UiShellProps> = ({ children, navItemColor }) => {
  // State and hooks
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const controls = useAnimation();
  const { isVisible, scrollPosition } = useScrollVisibility(isSmall);

  // Handle scroll-based animations
  React.useEffect(() => {
    if (!isSmall) {
      controls.start({
        y: isVisible ? MOTION_INITIAL_Y : MOTION_HIDDEN_Y,
        transition: { duration: 0.3, ease: "easeInOut" },
      });
    }
  }, [isVisible, controls, isSmall]);

  // Event handlers
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  // Compute dynamic styles based on scroll position
  const appBarColor =
    scrollPosition > SCROLL_THRESHOLD
      ? theme.palette.mode === "dark"
        ? `rgba(0,0,0,0.4)`
        : `rgba(255,255,255,0.8)`
      : "transparent";
  const textColor =
    scrollPosition > SCROLL_THRESHOLD
      ? theme.palette.primary.main
      : navItemColor ?? "#fff";

  // Mobile drawer content
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Box mb={2}>
        <Logo isSmall={true} />
      </Box>
      <Divider />
      <List>
        {navItems.map((item, idx) => (
          <ListItem key={idx} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center" }}
              onClick={() => navigate(item.url)}
              aria-label={`Navigate to ${item.name} page`}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton
            sx={{ textAlign: "center" }}
            onClick={() => navigate(LOGIN)}
            aria-label="Login to your account"
          >
            <ListItemText primary="Login" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      {/* Render AppBar with or without animation based on screen size */}
      <AppBar
        component="nav"
        position="fixed"
        elevation={0}
        sx={{
          background: isSmall ? "black" : appBarColor,
          transition: "background-color 0.3s ease-in-out",
        }}
      >
        <Box display="flex" justifyContent={`space-between`}>
          <Box>
            <Logo
              isSmall={isSmall}
              useWhiteLogo={scrollPosition <= SCROLL_THRESHOLD}
            />
          </Box>

          <Box>
            {isSmall ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  ml: 2,
                  position: "absolute",
                  right: theme.spacing(2),
                }}
              >
                <MenuIcon color="primary" />
              </IconButton>
            ) : (
              <Box>
                <NavigationItems
                  navItems={navItems}
                  textColor={isSmall ? theme.palette.primary.main : textColor}
                />
              </Box>
            )}
          </Box>
        </Box>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{ p: 0 }}>
        {children}
      </Box>
    </>
  );
};
